import { inr } from './money';
import { calc, advanceFor, balanceDue, span } from './pricing';
import { phase } from './availability';
import { fDate, fDT, fRange } from './format';
import { firstName, phoneKey } from './customers';
import type { BookingRecord, Customer, Settings } from './types';

export function renderMessage(key: string, r: BookingRecord, c: Customer, s: Settings): string {
  const t = s.templates[key];
  if (!t) return '';
  const k = calc(r), sp = span(r), P = s.property, d = r.deposit;
  const pays = r.payments.filter(p => !p.voided && p.type !== 'refund'), last = pays[pays.length - 1];
  // A Customize item with no agreed price yet reads "to be agreed", and so do the total and advance.
  const tba = 'to be agreed after discussion';
  const lines = r.items.map(i => `${i.pkgName}: ${i.custom && i.agreed <= 0 ? tba : inr(i.agreed)}\n`).join('')
    + (k.extra ? `Extra guests: ${inr(k.extra)}\n` : '') + (k.clean ? `Cleaning: ${inr(k.clean)}\n` : '')
    + (k.disc ? `Discount: −${inr(k.disc)}\n` : '') + (k.damage ? `Damage beyond deposit: ${inr(k.damage)}\n` : '');
  const lastItem = r.items[r.items.length - 1]!;
  const pkg = s.packages.find(p => p.id === lastItem.pkg);
  const v: Record<string, string | number> = {
    first: firstName(c), name: c.name, id: r.number ?? 'pending', type: r.type, typeLower: r.type.toLowerCase(),
    dates: fRange(sp.start, sp.end), package: r.items.map(i => i.pkgName).join(' + '), guests: r.guests,
    chargeLines: lines, total: k.pending ? tba : inr(k.total), paid: inr(k.paid), balance: inr(Math.max(0, k.balance)),
    advance: k.pending ? `${r.terms.advanceMode === 'percent' ? r.terms.advancePct + '% of the agreed price' : inr(r.terms.advance)}` : inr(advanceFor(k.total, r.terms)), refundWindow: r.terms.refundWindow, balanceDue: fDate(balanceDue(r)),
    deposit: inr(d.required), holdExpiry: r.hold ? fDT(r.hold.expires) : '—', upi: P.upi, phone: P.phone, maps: P.maps,
    caretaker: P.caretaker, checkin: fDT(sp.start), checkout: fDT(sp.end), areas: (pkg?.areas ?? []).join(', '),
    lastPayment: last ? inr(last.amount) : '—', lastMode: last ? last.mode : '—', lastDate: last ? fDate(last.date) : '—',
    depositCollected: inr(k.dCollected || k.dRequired),
    deductionLines: d.deductions.map(x => `Deduction — ${x.reason}: −${inr(x.amount)}\n`).join(''),
    refundAmount: inr(d.refund?.amount ?? Math.max(0, k.dCollected - k.dDeducted)), refundMode: d.refund?.mode ?? 'UPI',
    cancelPaid: inr(r.cancel?.paid ?? k.paid), cancelRefund: inr(r.cancel?.refund ?? 0), cancelRetained: inr(r.cancel?.retained ?? 0),
  };
  return t.text.replace(/\{(\w+)\}/g, (_, x: string) => String(v[x] ?? ''));
}

export function suggestTemplates(r: BookingRecord, now: number): string[] {
  const ph = phase(r, now), k = calc(r);
  switch (ph) {
    case 'enquiry': return ['quotation', 'hold'];
    case 'hold': return ['hold', 'quotation', 'confirmation'];
    case 'expired': return ['quotation'];
    case 'upcoming': return [k.balance > 0 ? 'reminder' : 'payment', r.kind === 'stay' ? 'prearrival' : 'event', 'confirmation', 'advance'];
    case 'inprogress': return ['deposit', k.balance > 0 ? 'reminder' : 'payment'];
    case 'closing': return [k.balance > 0 ? 'reminder' : 'refund', 'thanks'];
    case 'completed': return ['thanks', 'refund'];
    case 'cancelled': return ['cancel'];
    default: return ['quotation'];
  }
}

/** wa.me deep link. Opening it is not evidence the message was sent. */
export function whatsappLink(number: string, text: string): string {
  const k = phoneKey(number);
  const intl = k.length === 10 ? '91' + k : number.replace(/\D/g, '');
  return `https://wa.me/${intl}?text=${encodeURIComponent(text)}`;
}
